import { useFeatureValue } from '@growthbook/growthbook-react';
import Grid from '@mui/material/Grid';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { CharactersCounter } from '@openx/components-core/lib/CharactersCounter/CharactersCounter.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import { ALLOW_ORGANIZATION_DSP_RESTRICTION_MODES } from '@openx/constants/feature-flag-keys.ts';
import { type OrganizationType, organizationType } from '@openx/types/organization.ts';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { allowedCountriesOptions, getAllowedCountriesLabels, organizationFields } from '../../../constants.ts';
import type { OrganizationFormData } from '../../../types.ts';
import { AccountHierarchyPreview } from './components/AccountHierarchyPreview.tsx';
import { OrganizationTypeField } from './components/OrganizationTypeField.tsx';
import { ParentOrganizationField } from './components/ParentOrganizationField.tsx';
import { DspRestrictionSection } from './DspRestrictionSection.tsx';
import { DspRestrictionExtendedSection } from './DspRestrictionSectionExtended.tsx';

type OrganizationDetailsSectionsProps = {
  isEdit?: boolean;
  initialFormValues: OrganizationFormData;
};

export const OrganizationDetailsSections = ({ isEdit, initialFormValues }: OrganizationDetailsSectionsProps) => {
  const isDspRestrictionModesEnabled = useFeatureValue(ALLOW_ORGANIZATION_DSP_RESTRICTION_MODES, false);
  const { t } = useTranslation();
  const allowedCountriesOptionsLabels = getAllowedCountriesLabels(t);

  const observableDescription = useWatch<OrganizationFormData, typeof organizationFields.DESCRIPTION>({
    name: organizationFields.DESCRIPTION,
  });

  const observableOrganizationType = useWatch<OrganizationFormData, typeof organizationFields.ORGANIZATION_TYPE>({
    name: organizationFields.ORGANIZATION_TYPE,
  });

  const observableParentAccount = useWatch<OrganizationFormData, typeof organizationFields.PARENT_ACCOUNT>({
    name: organizationFields.PARENT_ACCOUNT,
  });

  const canManageDspRestrictions = (
    [organizationType.Buyer, organizationType.TechPartner] as OrganizationType[]
  ).includes(observableOrganizationType as OrganizationType);

  return (
    <FormSection divider title={t('Organization Details')} rightColumnSize={9}>
      <Grid container spacing={2}>
        <Grid size={6}>
          <ParentOrganizationField isEdit={isEdit} />

          <RHFTextField
            name={organizationFields.NAME}
            label={t('Organization Name')}
            placeholder={t('Enter Organization Name')}
            fullWidth
            dataTest="organization-name"
          />

          <OrganizationTypeField initialFormValues={initialFormValues} isEdit={isEdit} />

          <RHFAutoCompleteVirtualize
            name={organizationFields.ALLOWED_DOMAINS}
            limitTags={6}
            multiple
            freeSolo
            options={[]}
            sx={{ mt: 2 }}
            textFieldProps={{
              label: t('Allowed Email Domains'),
              placeholder: t('Enter Allowed Domains'),
            }}
            data-test="allowed-domains"
          />

          <RHFTextField
            name={organizationFields.DESCRIPTION}
            label={t('Organization Description')}
            placeholder={t('Enter Organization Description')}
            fullWidth
            multiline
            rows={4}
            slotProps={{ htmlInput: { maxLength: 1000 }, formHelperText: { component: 'div' } }}
            helperText={<CharactersCounter maxValue={1000} currentValue={observableDescription?.length} />}
            sx={{
              mt: 2,
            }}
            dataTest="description"
          />

          <RHFAutoCompleteVirtualize
            name={organizationFields.ALLOWED_COUNTRIES}
            getOptionLabel={option => allowedCountriesOptionsLabels[option]}
            limitTags={6}
            multiple
            options={allowedCountriesOptions}
            sx={{ mb: 2 }}
            textFieldProps={{
              label: t('Allowed Countries'),
              placeholder: t('[ Select Allowed Countries ]'),
            }}
            data-test="allowed-countries"
            disabled={isEdit}
          />

          {canManageDspRestrictions &&
            (isDspRestrictionModesEnabled ? (
              <DspRestrictionExtendedSection isEdit={isEdit} initialFormValues={initialFormValues} />
            ) : (
              <DspRestrictionSection isEdit={isEdit} initialFormValues={initialFormValues} />
            ))}
        </Grid>

        {observableParentAccount && !isEdit && (
          <AccountHierarchyPreview selectedParentAccount={observableParentAccount} />
        )}
      </Grid>
    </FormSection>
  );
};
